import { GetFleetStatus } from './GraphQL';

// Fleet-wide status data access. One precomputed card per fridge — status,
// vitals and the 24h sparkline in one shape. See `libs/heartbeatHistory.js` for
// how to read the sparkline itself.

// AppSync rejects the whole query above 100 unique ids, so page the fleet.
export const FLEET_STATUS_CHUNK = 100;

/**
 * @typedef {Object} FleetStatusResult
 * @property {Record<string, Object>} byId locationId -> card. A fridge with no
 *   row is simply absent — expected, not a failure, so it doesn't set `partial`.
 * @property {boolean} partial A chunk came back incomplete (throttled partial
 *   read, or one chunk failed while others succeeded). Callers must say so
 *   rather than presenting a short list as the whole fleet.
 */

/**
 * Fetch fleet status cards for any number of locations. Dedupes (DynamoDB
 * rejects duplicate keys), chunks to the cap, and matches results back by
 * `locationId` — never by index, since the response may be shorter.
 *
 * @param {string[]} locationIds
 * @returns {Promise<FleetStatusResult>}
 * @throws When every chunk failed — a real error, not a partial read.
 */
export default async function fetchFleetStatus(locationIds) {
  const ids = Array.from(new Set((locationIds || []).filter(Boolean)));
  // Valid query, but no reason to spend a round trip — e.g. called pre-load.
  if (ids.length === 0) return { byId: {}, partial: false };

  const chunks = [];
  for (let i = 0; i < ids.length; i += FLEET_STATUS_CHUNK) {
    chunks.push(ids.slice(i, i + FLEET_STATUS_CHUNK));
  }

  const results = await Promise.all(
    chunks.map(async (chunk) => {
      try {
        const { fleet, errors } = await GetFleetStatus(chunk);
        return { fleet, incomplete: errors.length > 0, error: null };
      } catch (e) {
        // One bad chunk shouldn't blank the page; the caller flags the gap.
        console.error('getFleetStatus chunk failed', e);
        return { fleet: [], incomplete: true, error: e };
      }
    }),
  );

  const failures = results.map((r) => r.error).filter(Boolean);
  if (failures.length === chunks.length) throw failures[0];

  const byId = {};
  results.forEach(({ fleet }) => {
    fleet.forEach((card) => {
      if (card?.locationId) byId[card.locationId] = card;
    });
  });

  return { byId, partial: results.some((r) => r.incomplete) };
}
